import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { SignalProvider } from '../realtime/signal-provider.interface';
import { DrawingProgress } from '../realtime/signal.types';

/** Backend processing_status values. */
const STATUS_FAILED = -1;
const STATUS_COMPLETED = 2;

/** Drawings tab index on the bid detail screen (DetailBidComponent.DRAWINGS_TAB_INDEX). */
const DRAWINGS_TAB_INDEX = 4;

/** Most cards to keep on screen at once; older ones drop off the bottom. */
const MAX_VISIBLE = 4;

/** One "drawing finished" card. */
export interface DrawingNotification {
  id: string;
  documentId: number;
  bidId: number | null;
  title: string;
  fileName: string;
  message: string;
  failed: boolean;
}

/**
 * Announces drawing completions anywhere in the app.
 *
 * Watches the same Firebase feed the drawings screen uses and publishes cards through
 * notifications$, which DrawingNotificationsComponent renders at the app root. Owning the
 * rendering (rather than going through ngx-toastr) keeps the placement under our control —
 * the toastr version sat at the very top of the viewport and overlapped the app header.
 */
@Injectable({ providedIn: 'root' })
export class DrawingCompletionNotifier {
  private readonly subject = new BehaviorSubject<DrawingNotification[]>([]);
  readonly notifications$: Observable<DrawingNotification[]> = this.subject.asObservable();

  private subscription: Subscription | null = null;
  private familyRootId: number | null = null;

  /**
   * Set on start(), passed straight from the logged-in session — see
   * RealtimeSessionService.start(), which already has this value in scope for
   * every call site (login, resume, office switch).
   *
   * null means "no requirement was given" (should not happen in practice, since
   * start() is only ever called with a real session), and is treated the same
   * as a signal with no user_id: show to everyone, matching the behaviour
   * before this filter existed.
   */
  private currentUserId: number | null = null;

  /**
   * document_id -> the `ts` already announced.
   *
   * The feed re-emits the whole node on every write, so without this a single completion
   * would raise a card again each time any other drawing in the office progressed.
   */
  private notified = new Map<number, number>();

  /**
   * Whether the first snapshot has been consumed. That snapshot lists everything finished in
   * the last hour, so announcing from it would greet the user with a stack of stale cards.
   */
  private seeded = false;

  constructor(private signalProvider: SignalProvider, private router: Router) {}

  start(familyRootId: number, currentUserId?: number | null): void {
    if (this.subscription && this.familyRootId === familyRootId) {
      return; // already watching this office
    }

    this.stop();
    this.familyRootId = familyRootId;
    this.currentUserId = currentUserId ?? null;

    this.subscription = this.signalProvider
      .watchValues<DrawingProgress>(familyRootId, 'drawings')
      .subscribe({
        next: (byDoc) => this.handle(byDoc),
        // A dead feed must not break the app; the drawings screen still shows status on load.
        error: () => this.stop(),
      });
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.familyRootId = null;
    this.currentUserId = null;
    this.notified.clear();
    this.seeded = false;
    this.subject.next([]);
  }

  dismiss(id: string): void {
    this.subject.next(this.subject.value.filter((n) => n.id !== id));
  }

  /** Open the drawing's pages, then clear the card. */
  view(item: DrawingNotification): void {
    this.dismiss(item.id);

    if (!item.bidId) {
      return;
    }

    // `tab` is the deep-link parameter DetailBidComponent already honours; `drawingDoc` is
    // read by the drawings screen to open this document's pages on arrival.
    this.router.navigate(['/bids/bid', item.bidId], {
      queryParams: { tab: DRAWINGS_TAB_INDEX, drawingDoc: item.documentId },
    });
  }

  private handle(byDoc: Record<string, DrawingProgress>): void {
    const entries = Object.values(byDoc ?? {});

    if (!this.seeded) {
      entries.forEach((p) => {
        if (this.isTerminal(p)) {
          this.notified.set(p.document_id, p.ts);
        }
      });
      this.seeded = true;

      return;
    }

    entries.forEach((p) => {
      if (!this.isTerminal(p) || this.notified.get(p.document_id) === p.ts) {
        return;
      }

      // Recorded regardless of ownership: this ts has been evaluated for this
      // document, so a later identical snapshot (the whole node re-emits on any
      // drawing's progress) does not re-run the ownership check for nothing.
      this.notified.set(p.document_id, p.ts);

      if (this.isMine(p)) {
        this.push(p);
      }
    });
  }

  /**
   * Whether this completion belongs to the current user.
   *
   * Fail-open: a signal with no user_id (a legacy row, or one pushed before this
   * field existed / before the backend's document had a known creator) is shown
   * to everyone — the same behaviour as every notification before this filter
   * existed, so it can only add filtering, never make an existing case go
   * silently missing.
   */
  private isMine(p: DrawingProgress): boolean {
    return p.user_id == null || p.user_id === this.currentUserId;
  }

  private isTerminal(p: DrawingProgress): boolean {
    return p?.status === STATUS_COMPLETED || p?.status === STATUS_FAILED;
  }

  private push(p: DrawingProgress): void {
    const failed = p.status === STATUS_FAILED;

    const message = failed
      ? 'Processing did not complete. Open the drawing to see why.'
      : `${p.total} page${p.total === 1 ? '' : 's'} ready to view` +
        (p.failed > 0 ? ` · ${p.failed} could not be read` : '');

    const card: DrawingNotification = {
      id: `${p.document_id}:${p.ts}`,
      documentId: p.document_id,
      bidId: p.bid_id,
      title: failed ? 'Drawing processing failed' : 'Drawing ready',
      fileName: p.file_name || 'Drawing',
      message,
      failed,
    };

    // Newest first, replacing any earlier card for the same document.
    const rest = this.subject.value.filter((n) => n.documentId !== p.document_id);

    this.subject.next([card, ...rest].slice(0, MAX_VISIBLE));
  }
}
